import Hero from '../components/home/Hero.jsx'
import Demos from '../components/home/Demos.jsx'
import Features from '../components/home/Features.jsx'
import Pricing from '../components/home/Pricing.jsx'
import FAQ from '../components/home/FAQ.jsx'
import LandingNavbar from '../components/layout/LandingNavbar.jsx'
import { Link } from 'react-router-dom'
import { ArrowRight, BadgeCheck, Sparkles } from 'lucide-react'

export default function Home() {
  const highlights = [
    {
      title: 'Automatisation intelligente',
      description: 'Prospection, contenus et suivi dans une seule expérience fluide.',
      icon: Sparkles,
    },
    {
      title: 'Conforme et public',
      description: 'Une page d’accueil claire avec les liens légaux attendus par Google.',
      icon: BadgeCheck,
    },
    {
      title: 'Accès rapide',
      description: 'Un parcours simple vers la connexion et la découverte de la plateforme.',
      icon: ArrowRight,
    },
  ]

  return (
    <div className="relative overflow-x-hidden bg-[#040404] text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-10%] top-[-8rem] h-80 w-80 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute right-[-10%] top-24 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/50 to-transparent" />
      </div>

      <LandingNavbar />
      <main>
        <section className="relative mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8 lg:pt-14">
          <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.03] px-6 py-12 shadow-2xl shadow-black/30 backdrop-blur sm:px-10 sm:py-14 lg:px-14 lg:py-16">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(91,92,244,0.10),transparent_30%),radial-gradient(circle_at_top_right,rgba(245,158,11,0.10),transparent_35%)]" />

            <div className="relative grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(480px,1.05fr)] lg:gap-18">
              <div className="relative z-10 min-w-0 space-y-8">
                <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-indigo-400/20 bg-indigo-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-100">
                  <Sparkles className="h-4 w-4 shrink-0" />
                  <span className="truncate">JeanlouisPublic</span>
                </div>

                <div className="space-y-5 min-w-0">
                  <h1 className="max-w-4xl text-balance text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">
                    Une base claire, élégante, et pensée pour vos projets internes.
                  </h1>
                  <p className="max-w-3xl text-pretty text-base leading-8 text-slate-300 sm:text-lg">
                    JeanlouisPublic rassemble la prospection, la génération de contenus et la gestion
                    des documents dans une interface sobre, lisible et rapide à parcourir.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/login"
                    className="inline-flex max-w-full items-center gap-2 rounded-full bg-indigo-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-indigo-300"
                  >
                    Se connecter
                    <ArrowRight className="h-4 w-4 shrink-0" />
                  </Link>
                  <a
                    href="#hero"
                    className="inline-flex max-w-full items-center gap-2 rounded-full border border-white/10 px-5 py-3 text-sm font-medium text-white transition hover:border-amber-300/40 hover:bg-white/5"
                  >
                    Voir la démonstration
                  </a>
                </div>

                <div className="flex flex-wrap gap-3 text-sm text-slate-400">
                  <Link
                    to="/legal/politique-de-confidentialite"
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 transition hover:border-amber-300/30 hover:bg-white/10 hover:text-white"
                  >
                    Politique de confidentialité
                  </Link>
                  <Link
                    to="/legal/cgu"
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 transition hover:border-amber-300/30 hover:bg-white/10 hover:text-white"
                  >
                    Conditions d’utilisation
                  </Link>
                  <Link
                    to="/legal/mentions-legales"
                    className="rounded-full border border-white/10 bg-white/5 px-4 py-2 transition hover:border-amber-300/30 hover:bg-white/10 hover:text-white"
                  >
                    Mentions légales
                  </Link>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  {highlights.map((item) => {
                    const Icon = item.icon
                    return (
                      <article key={item.title} className="min-w-0 rounded-2xl border border-white/10 bg-black/20 p-4">
                        <div className="mb-4 inline-flex rounded-xl border border-indigo-400/20 bg-indigo-400/10 p-2 text-indigo-100">
                          <Icon className="h-4 w-4" />
                        </div>
                        <h2 className="break-words text-sm font-semibold text-white">{item.title}</h2>
                        <p className="mt-2 break-words text-xs leading-6 text-slate-400">{item.description}</p>
                      </article>
                    )
                  })}
                </div>

                <div className="rounded-2xl border border-amber-300/15 bg-amber-300/8 px-4 py-3 text-sm leading-7 text-slate-300">
                  Une landing plus sobre, plus stable, et plus conforme avec les liens légaux visibles dès le départ.
                </div>
              </div>

              <div className="pointer-events-none relative hidden w-full justify-self-stretch lg:block">
                <div className="absolute -inset-4 rounded-[2rem] bg-indigo-500/10 blur-2xl" />
                <div className="relative w-full rounded-[2rem] border border-white/10 bg-black/35 p-6 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-7">
                  <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/70 p-6">
                    <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-200">
                          Aperçu de la plateforme
                        </p>
                        <p className="mt-2 break-words text-lg font-semibold text-white">
                          Un espace pensé pour être lisible dès l’arrivée.
                        </p>
                      </div>
                      <div className="rounded-full border border-indigo-400/20 bg-indigo-400/10 px-3 py-1 text-xs font-medium text-indigo-100">
                        Online
                      </div>
                    </div>

                    <div className="mt-5 grid gap-3 sm:grid-cols-3">
                      {highlights.map((item) => {
                        const Icon = item.icon
                        return (
                          <article key={item.title} className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                            <div className="mb-4 inline-flex rounded-xl border border-indigo-400/20 bg-indigo-400/10 p-2 text-indigo-100">
                              <Icon className="h-4 w-4" />
                            </div>
                            <h2 className="break-words text-sm font-semibold text-white">{item.title}</h2>
                            <p className="mt-2 break-words text-xs leading-6 text-slate-400">{item.description}</p>
                          </article>
                        )
                      })}
                    </div>

                    <div className="mt-5 rounded-2xl border border-indigo-400/15 bg-indigo-400/8 p-4 text-sm leading-7 text-slate-300">
                      Une landing plus élégante, un accès plus clair, et les pages légales visibles pour rester propre côté Google OAuth.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="hero">
          <Hero />
        </section>
        <Demos />
        <Features />
        <Pricing />
        <FAQ />
        <footer className="mx-auto max-w-6xl px-4 pb-10 pt-8 text-sm text-slate-400 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4 border-t border-white/10 pt-6">
            <span> {new Date().getFullYear()} JeanlouisPublic</span>
            <Link to="/legal/mentions-legales" className="transition hover:text-white">
              Mentions légales
            </Link>
            <Link to="/legal/politique-de-confidentialite" className="transition hover:text-white">
              Politique de confidentialité
            </Link>
            <Link to="/legal/cgu" className="transition hover:text-white">
              CGU
            </Link>
          </div>
        </footer>
      </main>
    </div>
  )
}
